import { useMemo } from 'react';

import useOverwatchAccess from 'containers/Overwatch/permissions/useOverwatchAccess';
import { collectionPage, fieldCode, fieldText } from 'containers/Overwatch/widgets/adapters';
import { OVERWATCH_VALUE_STATES } from 'containers/Overwatch/widgets/types';
import useOverwatchVisibility from 'containers/Overwatch/widgets/useOverwatchVisibility';
import useOverwatchWidgetData from 'containers/Overwatch/widgets/useOverwatchWidgetData';
import { OW_CARD_WIDGETS } from 'containers/Overwatch/widgets/widgetMap';

export interface IOrbitUnsubscribedFeature {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  category: { code: string | null; label: string } | null;
  statusLabel: string;
}

export type OrbitUnsubscribedState =
  | { status: 'loading' }
  | { status: 'hidden' }
  | { status: 'ready'; features: IOrbitUnsubscribedFeature[] };

const WIDGET = OW_CARD_WIDGETS.discoverableCapabilities;
const DATA_KEY = 'capabilities';

/** Explore More with Perx items the tenant does not subscribe to; hidden whenever access or the source is in doubt. */
const useOrbitUnsubscribedFeatures = (): OrbitUnsubscribedState => {
  const { visibility, widgets: discovered } = useOverwatchVisibility();
  const { sections } = useOverwatchAccess();
  const listed = discovered.some(item => item.key === WIDGET.key);
  const enabled = visibility === 'visible' && sections.exploreMore && listed;
  const { widgets } = useOverwatchWidgetData(enabled ? [WIDGET.key] : [], { enabled });
  const widget = widgets[WIDGET.key];

  return useMemo<OrbitUnsubscribedState>(() => {
    if (visibility === 'loading') return { status: 'loading' };
    if (!enabled) return { status: 'hidden' };
    if (!widget || widget.status === 'loading') return { status: 'loading' };

    const envelope = widget.envelopes?.[DATA_KEY];

    if (widget.status !== 'completed' || !envelope || !OVERWATCH_VALUE_STATES.has(envelope.state)) {
      return { status: 'hidden' };
    }

    const seen = new Set<string>();
    const features = collectionPage(envelope, envelope.data).items.reduce<
      IOrbitUnsubscribedFeature[]
    >((acc, item) => {
      const id = String(item.id);

      if (seen.has(id) || fieldCode(item, 'subscription_status') !== 'not_subscribed') return acc;
      seen.add(id);
      const description = fieldText(item, 'description') ?? '';
      const categoryLabel = fieldText(item, 'category');
      acc.push({
        id,
        title: fieldText(item, 'name') ?? id,
        subtitle: description || categoryLabel || '',
        description,
        category: categoryLabel
          ? { code: fieldCode(item, 'category'), label: categoryLabel }
          : null,
        statusLabel: fieldText(item, 'subscription_status') ?? 'Not Subscribed'
      });

      return acc;
    }, []);

    return { status: 'ready', features };
  }, [visibility, enabled, widget]);
};

export default useOrbitUnsubscribedFeatures;
